import React, { useState, useEffect } from 'react';
import { supabase } from '../api/supabase';
import { Box, Typography, Paper, List, ListItem, ListItemText, ListItemAvatar, Avatar, Collapse, Divider, CircularProgress } from '@mui/material';
import Loader from '../components/common/Loader';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CakeIcon from '@mui/icons-material/Cake';

// Componente que busca os detalhes de UMA venda específica
const SaleDetails = ({ saleId }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchSaleItems = async () => {
            if (!saleId) return;
            setLoading(true);
            // Query simples e segura para os itens
            const { data, error } = await supabase
                .from('sale_items')
                .select('quantity, price_at_sale, products(name)')
                .eq('sale_id', saleId);

            if (error) console.error("Error fetching sale items:", error);
            else setItems(data || []);
            setLoading(false);
        };
        fetchSaleItems();
    }, [saleId]);

    if (loading) return <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}><CircularProgress size={20} /></Box>;

    return (
        <List component="div" disablePadding>
            {items.map((item, index) => (
                <ListItem key={index} sx={{ pl: 7 }}>
                    <ListItemAvatar><Avatar sx={{ bgcolor: 'transparent' }}><CakeIcon color="action" /></Avatar></ListItemAvatar>
                    <ListItemText primary={`${item.quantity}x ${item.products ? item.products.name : 'Produto Removido'}`} secondary={`Preço na venda: ${Number(item.price_at_sale).toLocaleString('pt-BR', { style: 'currency', currency: 'BR' })}`} />
                </ListItem>
            ))}
        </List>
    );
};

// Componente para cada linha de venda
const SaleListItem = ({ sale }) => {
    const [open, setOpen] = useState(false);
    return (
        <React.Fragment>
            <ListItem button onClick={() => setOpen(!open)} sx={{ py: 1.5 }}>
                <ListItemAvatar><Avatar sx={{ bgcolor: 'primary.light' }}><ReceiptLongIcon /></Avatar></ListItemAvatar>
                <ListItemText primary={`Venda #${sale.id} - ${Number(sale.total_amount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`} secondary={`Cliente: ${sale.clients ? sale.clients.name : 'Não informado'} | Pagamento: ${sale.payment_method}`} />
            </ListItem>
            <Collapse in={open} timeout="auto" unmountOnExit>
                {open && <SaleDetails saleId={sale.id} />}
            </Collapse>
        </React.Fragment>
    );
};

const SalesHistoryPage = () => {
  const [salesByDate, setSalesByDate] = useState({});
  const [loading, setLoading] = useState(true);

  const groupSalesByDate = (sales) => {
    return sales.reduce((acc, sale) => {
      const saleDate = new Date(sale.created_at).toLocaleDateString('pt-BR', { year: 'numeric', month: 'long', day: 'numeric' });
      if (!acc[saleDate]) { acc[saleDate] = []; }
      acc[saleDate].push(sale);
      return acc;
    }, {});
  };

  useEffect(() => {
    const fetchSales = async () => {
      setLoading(true);
      // QUERY PRINCIPAL SIMPLIFICADA: busca apenas as vendas e o nome do cliente
      const { data, error } = await supabase.from('sales').select('*, clients(name)').order('created_at', { ascending: false });
      if (error) { console.error('Error fetching sales:', error); } 
      else { setSalesByDate(groupSalesByDate(data || [])); }
      setLoading(false);
    };
    fetchSales();
  }, []);

  if (loading) return <Loader />;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Histórico de Vendas</Typography>
      {Object.keys(salesByDate).length > 0 ? (
        Object.entries(salesByDate).map(([date, sales]) => (
          <Box key={date} sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ pl: 2, pb: 1, borderBottom: '1px solid #eee' }}>{date}</Typography>
            <Paper>
              <List sx={{ p: 0 }}>
                {sales.map((sale, index) => (
                  <React.Fragment key={sale.id}>
                    <SaleListItem sale={sale} />
                    {index < sales.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          </Box>
        ))
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}><Typography color="text.secondary">Nenhuma venda registrada ainda.</Typography></Paper>
      )}
    </Box>
  );
};

export default SalesHistoryPage;